import { toNumericDurationValue } from '../converter/to-numeric-duration-value'
import { isIso8601Duration } from '../validator/is-iso-8601-duration'

/**
 * Helper function to formats a ISO 8601 duration string into a human readable string of hours and minutes that suits
 * the requirements for resting time specifications. Zero-values will return NULL. The minimum duration is 1 minute,
 * rounding down. E.g.: 9 hours, 9 h 30 min, -5 min.
 *
 * @param {Intl} intl - The modux intl helper.
 * @param {string} iso8601DurationString - A ISO 8601 duration string.
 * @returns {string|null} A human readable time string for resting times.
 */
export function formatISO8601Duration(intl, iso8601DurationString) {
    let formattedDuration = ''
    const isValidDuration = isIso8601Duration(iso8601DurationString)
    const durationMinutes = toNumericDurationValue(iso8601DurationString, 'minutes')

    if (!isValidDuration || durationMinutes === 0) {
        return null
    }

    const durationMinutesAbsolute = Math.abs(durationMinutes)
    const fullHours = Math.floor(durationMinutesAbsolute / 60)
    const leftoverMinutes = durationMinutesAbsolute % 60

    // Add a signed prefix for negative values
    formattedDuration += Math.sign(durationMinutes) < 0 ? '-' : ''

    if (fullHours > 0) {
        // If there is no fraction of minutes the time unit label should be 'hour(s)'.
        let hoursUnitLabel = intl.translate('atl_hours')
        if (leftoverMinutes > 0) {
            hoursUnitLabel = `${intl.translate('atl_hours_short')} `
        } else if (Math.abs(fullHours) === 1) {
            hoursUnitLabel = intl.translate('atl_hour')
        }
        formattedDuration += `${fullHours} ${hoursUnitLabel}`
    }

    if (leftoverMinutes > 0) {
        const minutesUnitLabel = intl.translate('atl_minutes_unit_short')
        formattedDuration += `${leftoverMinutes} ${minutesUnitLabel}`
    }

    return formattedDuration
}
